import React from "react";

export default function SplashScreen({ onClick }) {
  return (
    <>
      <style>{`
        /* =========================================================
           GLOBAL
        ========================================================= */

        * {
          box-sizing: border-box;
        }
          

        body {
          margin: 0;
          padding: 0;
          overflow-x: hidden;
          font-family:
            -apple-system,
            BlinkMacSystemFont,
            "SF Pro Display",
            "SF Pro Text",
            "Segoe UI",
            Roboto,
            Helvetica,
            Arial,
            sans-serif;
        }

        /* =========================================================
           MAIN BACKGROUND
        ========================================================= */

        .bro-splash-wrapper {
          width: 100%;
          min-height: 100vh;
          min-height: 100dvh;

          display: flex;
          justify-content: center;
          align-items: center;

          padding: 24px;

          position: relative;
          overflow: hidden;

          background:
            radial-gradient(
              circle at 50% 15%,
              rgba(255, 255, 255, 1) 0%,
              rgba(241, 245, 249, 1) 45%,
              rgba(226, 232, 240, 1) 100%
            );
        }

        /* =========================================================
           BACKGROUND GLOW
        ========================================================= */

        .bro-glow {
          position: absolute;

          width: 380px;
          height: 380px;

          border-radius: 50%;

          background:
            radial-gradient(
              circle,
              rgba(255, 115, 0, 0.15) 0%,
              rgba(0, 92, 169, 0.10) 45%,
              transparent 72%
            );

          filter: blur(35px);

          pointer-events: none;

          animation: glowMove 6s ease-in-out infinite alternate;
        }

        @keyframes glowMove {
          0% {
            transform: translate(-80px, -80px) scale(0.9);
          }

          100% {
            transform: translate(80px, 80px) scale(1.1);
          }
        }

        /* =========================================================
           DEVICE / GLASS FRAME
        ========================================================= */

        .bro-device-frame {
          width: 100%;
          max-width: 420px;

          height: min(860px, calc(100vh - 48px));
          height: min(860px, calc(100dvh - 48px));

          position: relative;

          display: flex;
          flex-direction: column;

          overflow: hidden;

          border-radius: 46px;

          background:
            linear-gradient(
              145deg,
              rgba(255, 255, 255, 0.88),
              rgba(255, 255, 255, 0.58)
            );

          border:
            1px solid rgba(255, 255, 255, 0.95);

          box-shadow:
            0 35px 90px rgba(15, 23, 42, 0.13),
            0 12px 35px rgba(15, 23, 42, 0.08),
            inset 0 1px 0 rgba(255, 255, 255, 0.95),
            inset 0 -1px 0 rgba(255, 255, 255, 0.4);

          backdrop-filter: blur(35px);
          -webkit-backdrop-filter: blur(35px);
        }

        /* =========================================================
           GLASS SHINE
        ========================================================= */

        .bro-device-frame::before {
          content: "";

          position: absolute;

          top: 0;
          left: 0;

          width: 100%;
          height: 45%;

          pointer-events: none;

          background:
            linear-gradient(
              180deg,
              rgba(255, 255, 255, 0.55),
              transparent
            );

          opacity: 0.65;
        }

        /* =========================================================
           INNER SCROLL
        ========================================================= */

        .bro-scroll-container {
          width: 100%;
          height: 100%;

          overflow-y: auto;

          display: flex;
          justify-content: center;
          align-items: center;

          padding:
            max(30px, env(safe-area-inset-top))
            24px
            max(30px, env(safe-area-inset-bottom));

          position: relative;

          z-index: 2;

          scrollbar-width: thin;
          scrollbar-color:
            rgba(0, 92, 169, 0.15)
            transparent;
        }

        .bro-scroll-container::-webkit-scrollbar {
          width: 4px;
        }

        .bro-scroll-container::-webkit-scrollbar-track {
          background: transparent;
        }

        .bro-scroll-container::-webkit-scrollbar-thumb {
          background: rgba(0, 92, 169, 0.15);
          border-radius: 20px;
        }

        /* =========================================================
           MAIN CONTENT
        ========================================================= */

        .bro-splash-content {
          width: 100%;

          display: flex;
          flex-direction: column;

          justify-content: center;
          align-items: center;

          text-align: center;

          cursor: pointer;

          user-select: none;

          animation:
            splashAppear
            1s
            cubic-bezier(0.16, 1, 0.3, 1)
            forwards;

          transition:
            transform 0.25s
            cubic-bezier(0.16, 1, 0.3, 1);
        }

        .bro-splash-content:active {
          transform: scale(0.94);
        }

        @keyframes splashAppear {
          from {
            opacity: 0;
            transform:
              translateY(20px)
              scale(0.96);
          }

          to {
            opacity: 1;
            transform:
              translateY(0)
              scale(1);
          }
        }

        /* =========================================================
           LOGO AREA
        ========================================================= */

        .bro-logo-glass {
          width: 150px;
          height: 150px;

          display: flex;
          justify-content: center;
          align-items: center;

          border-radius: 44px;

          background:
            linear-gradient(
              145deg,
              rgba(255, 255, 255, 0.88),
              rgba(255, 255, 255, 0.42)
            );

          border:
            1px solid rgba(255, 255, 255, 0.9);

          box-shadow:
            0 25px 50px rgba(0, 92, 169, 0.10),
            inset 0 1px 0 rgba(255, 255, 255, 0.95);

          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);

          animation:
            logoFloat
            4s
            ease-in-out
            infinite;
        }

        @keyframes logoFloat {
          0%,
          100% {
            transform: translateY(0);
          }

          50% {
            transform: translateY(-8px);
          }
        }

        /* =========================================================
           SVG LOGO
        ========================================================= */

        .bro-logo-svg {
          width: 92px;
          height: 92px;

          filter:
            drop-shadow(
              0 10px 15px
              rgba(0, 92, 169, 0.16)
            );
        }

        /* =========================================================
           BRAND NAME
        ========================================================= */

        .bro-brand-name {
          margin: 28px 0 0;

          font-size: clamp(
            36px,
            9vw,
            46px
          );

          line-height: 1;

          font-weight: 500;

          letter-spacing: -1.5px;

          background:
            linear-gradient(
              120deg,
              #e53935 0%,
              #ff7300 48%,
              #ffb300 100%
            );

          -webkit-background-clip: text;
          background-clip: text;

          -webkit-text-fill-color: transparent;

          text-shadow:
            0 8px 25px
            rgba(255, 115, 0, 0.08);
        }

        /* =========================================================
           TAGLINE
        ========================================================= */

        .bro-tagline {
          margin-top: 15px;

          font-size: 12px;

          font-weight: 500;

          letter-spacing: 2.5px;

          text-transform: uppercase;

          color: rgba(15, 23, 42, 0.45);
        }

        /* =========================================================
           TAP INDICATOR
        ========================================================= */

        .bro-tap-indicator {
  margin-top: 42px;

  display: flex;
  justify-content: center;
  align-items: center;
}

/* Made In India Premium Badge */
.made-india-badge {
  display: inline-flex;
  align-items: center;
  gap: 9px;

  padding: 9px 15px;

  border-radius: 999px;

  background:
    linear-gradient(
      135deg,
      rgba(255, 153, 51, 0.12),
      rgba(255, 255, 255, 0.75),
      rgba(19, 136, 8, 0.12)
    );

  border: 1px solid rgba(0, 92, 169, 0.12);

  box-shadow:
    0 8px 25px rgba(15, 23, 42, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);

  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);

  animation: indiaBadgeFloat 3s ease-in-out infinite;
}

/* Indian Tricolor */
.india-flag {
  width: 22px;
  height: 15px;

  display: flex;
  flex-direction: column;

  overflow: hidden;

  border-radius: 2px;

  box-shadow:
    0 2px 5px rgba(15, 23, 42, 0.15);
}

.flag-saffron {
  height: 33.33%;
  background: #ff9933;
}

.flag-white {
  height: 33.33%;
  background: #ffffff;

  position: relative;
}

.flag-white::after {
  content: "✦";

  position: absolute;

  left: 50%;
  top: 50%;

  transform: translate(-50%, -50%);

  font-size: 7px;

  color: #000080;
}

.flag-green {
  height: 33.33%;
  background: #138808;
}

/* Text */
.made-india-text {
  font-size: 12px;

  font-weight: 700;

  letter-spacing: 0.7px;

  background:
    linear-gradient(
      90deg,
      #e87516 0%,
      #111827 48%,
      #138808 100%
    );

  -webkit-background-clip: text;
  background-clip: text;

  -webkit-text-fill-color: transparent;
}

/* India Pride */
.india-pride {
  font-size: 15px;

  line-height: 1;

  filter:
    drop-shadow(
      0 3px 5px rgba(15, 23, 42, 0.12)
    );
}

/* Soft floating effect */
@keyframes indiaBadgeFloat {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-3px);
  }
} 

        .bro-tap-dot {
          width: 7px;
          height: 7px;

          border-radius: 50%;

          background: #005ca9;

          box-shadow:
            0 0 0 7px rgba(0, 92, 169, 0.08);

          animation:
            tapPulse
            1.8s
            ease-in-out
            infinite;
        }

        @keyframes tapPulse {
          0% {
            transform: scale(0.8);
            box-shadow:
              0 0 0 5px
              rgba(0, 92, 169, 0.08);
          }

          50% {
            transform: scale(1);
            box-shadow:
              0 0 0 10px
              rgba(0, 92, 169, 0.02);
          }

          100% {
            transform: scale(0.8);
            box-shadow:
              0 0 0 5px
              rgba(0, 92, 169, 0.08);
          }
        }

        /* =========================================================
           DECORATIVE GLASS ORBS
        ========================================================= */

        .bro-orb {
          position: absolute;

          border-radius: 50%;

          pointer-events: none;

          filter: blur(1px);
        }

        .bro-orb-one {
          width: 90px;
          height: 90px;

          top: 8%;
          right: -35px;

          background:
            rgba(255, 115, 0, 0.08);
        }

        .bro-orb-two {
          width: 120px;
          height: 120px;

          bottom: 8%;
          left: -55px;

          background:
            rgba(0, 92, 169, 0.07);
        }

        /* =========================================================
           MOBILE
        ========================================================= */

        @media (max-width: 480px) {

          .bro-splash-wrapper {
            padding: 0;

            background:
              linear-gradient(
                145deg,
                #ffffff 0%,
                #f8fafc 100%
              );
          }

          .bro-device-frame {
            width: 100%;
            max-width: none;

            height: 100vh;
            height: 100dvh;

            border-radius: 0;

            border: none;

            box-shadow: none;

            background:
              linear-gradient(
                145deg,
                rgba(255, 255, 255, 0.98),
                rgba(248, 250, 252, 0.96)
              );
          }

          .bro-logo-glass {
            width: 135px;
            height: 135px;

            border-radius: 40px;
          }

          .bro-logo-svg {
            width: 82px;
            height: 82px;
          }

          .bro-brand-name {
            font-size: 38px;
            margin-top: 25px;
          }

          .bro-tagline {
            font-size: 10px;
            letter-spacing: 2px;
          }

          .bro-tap-indicator {
            margin-top: 34px;
          }
        }

        /* =========================================================
           SMALL MOBILE
        ========================================================= */

        @media (max-width: 360px) {

          .bro-logo-glass {
            width: 120px;
            height: 120px;
          }

          .bro-logo-svg {
            width: 74px;
            height: 74px;
          }

          .bro-brand-name {
            font-size: 34px;
          }

          .bro-tagline {
            font-size: 9px;
          }
        }

        /* =========================================================
           TABLET
        ========================================================= */

        @media (min-width: 481px) and (max-width: 1024px) {

          .bro-splash-wrapper {
            padding: 30px;
          }

          .bro-device-frame {
            max-width: 450px;

            height:
              min(
                900px,
                calc(100vh - 60px)
              );

            height:
              min(
                900px,
                calc(100dvh - 60px)
              );
          }

          .bro-logo-glass {
            width: 155px;
            height: 155px;
          }

          .bro-brand-name {
            font-size: 46px;
          }
        }

        /* =========================================================
           DESKTOP
        ========================================================= */

        @media (min-width: 1025px) {

          .bro-splash-wrapper {
            padding: 40px;
          }

          .bro-device-frame {
            max-width: 430px;

            height: min(
              880px,
              calc(100vh - 80px)
            );
          }

          .bro-splash-content:hover {
            transform: translateY(-3px);
          }

          .bro-logo-glass {
            width: 160px;
            height: 160px;
          }
        }

        /* =========================================================
           REDUCE MOTION
        ========================================================= */

        @media (prefers-reduced-motion: reduce) {

          .bro-glow,
          .bro-logo-glass,
          .bro-splash-content,
          .bro-tap-dot {
            animation: none;
          }

          .bro-splash-content {
            opacity: 1;
            transform: none;
          }
        }
      `}</style>

      {/* =========================================================
          SPLASH SCREEN
      ========================================================= */}

      <main className="bro-splash-wrapper">

        {/* Background Glow */}
        <div className="bro-glow"></div>

        {/* Decorative Orbs */}
        <div className="bro-orb bro-orb-one"></div>
        <div className="bro-orb bro-orb-two"></div>

        {/* =======================================================
            GLASS DEVICE
        ======================================================= */}

        <section className="bro-device-frame">

          <div className="bro-scroll-container">

            {/* =================================================
                CLICKABLE CONTENT
            ================================================= */}

            <div
              className="bro-splash-content"
              onClick={onClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  onClick?.();
                }
              }}
              aria-label="Continue to BroBizee"
            >

              {/* =================================================
    BROBIZEE LOGO IMAGE
================================================= */}

              <div className="bro-logo-glass">

                <img
                  src="/images/logo.png"
                  alt="BroBizee Logo"
                  className="bro-logo-image"
                />

              </div>


              {/* =================================================
                  TAGLINE
              ================================================= */}

              <div className="bro-tagline">
                Everything You Need
              </div>

              {/* =================================================
                  TAP INDICATOR
              ================================================= */}

              <div className="bro-tap-indicator">
                <span className="made-india-badge">


                  <span className="made-india-text">
                    Made In India
                  </span>

                </span>
              </div>

            </div>

          </div>

        </section>

      </main>
    </>
  );
}